/*
 * Kolmen viikon suunnitelma (T373; Toni 13.10.2026: «ensin näkymä (tuloste myöhemmin samasta), ikkuna kiinteä 3 viikkoa»).
 *
 * Yksi kohde, kolme viikkoa tästä viikosta eteenpäin. Ylhäällä työpaketit, joiden suunniteltu jakso (235, T520) osuu
 * ikkunaan — viikkosarakkeissa jana, rivillä tila ja «valmis linjatarkastukseen» -ilmoitukset. Paketti ilman suunniteltua
 * päivää ei piirry janalle vaan listataan erikseen «ei aikataulutettu» — mitattu tyhjä on eri asia kuin kolme viikkoa
 * tyhjää. Alhaalla kohteen tunnit viikoittain: tarve (resurssitarve × talon oletustyöpäivä, sama kaava kuin f_vaje),
 * allokoitu (allokaatio laji työ) ja vaje sanana. Ei uutta taulua; tilaajaversio ilman nimiä on oma vaihe (eiTilaajalle).
 */
import { useLataus, LatausTila } from "./Lataus";
import { useEffect, useState } from "react";
import { api, ApiError, type Kayttaja, type Kohde, type Suunnitelma as SuunnitelmaTieto } from "./api";
import { useKohdeSynkka } from "./Kohdekonteksti";
import { Tilasana } from "./Tila";
import { t, virheTeksti } from "./kieli";
import { naytaPvm } from "./pvm";
import { luku } from "./luvut";

/* T373 (Toni 15.10.2026 klo 18.25): «Ei aikataulutettu» -listaan alku- ja loppupäivä suoraan, yksittäin ja monivalinnalla. Sama reitti
   kuin Työpaketit-sivulla (PATCH /api/tyopaketit/:id, audit-liipaisin → sama muutoshistoria), samat roolit. Tyhjä päivä ei tallennu:
   kenttä jätetään pois pyynnöstä. loppu ≥ alku tarkistetaan ennen tallennusta (kannassa sama CHECK, 235). */
const MUOKKAAJAT = ["paakayttaja", "suunnittelija", "tyonjohtaja"];
function AikatauluttamattomatLista({ paketit, saa, paivita }: {
  paketit: { id: number; nro: number; nimi: string; tila: string }[]; saa: boolean; paivita: () => void;
}) {
  const [paivat, setPaivat] = useState<Record<number, { alku: string; loppu: string }>>({});
  const [valitut, setValitut] = useState<Set<number>>(new Set());
  const [yhteinen, setYhteinen] = useState({ alku: "", loppu: "" });
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);
  const ristiriita = (a: string, l: string) => !!a && !!l && l < a;
  async function tallenna(ids: number[], a: string, l: string) {
    if (!a && !l) return;
    if (ristiriita(a, l)) { setVirhe(t("su.loppu_ennen_alkua")); return; }
    setTallennetaan(true); setVirhe(null);
    const viat: string[] = [];
    for (const id of ids) {
      try { await api.muokkaaTyopaketti(id, { ...(a ? { suunniteltu_alku: a } : {}), ...(l ? { suunniteltu_loppu: l } : {}) }); }
      catch (e) { viat.push(`#${paketit.find((p) => p.id === id)?.nro ?? id}: ${e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)}`); }
    }
    setTallennetaan(false);
    if (viat.length) setVirhe(viat.join(" · "));
    setValitut(new Set()); setYhteinen({ alku: "", loppu: "" });
    paivita();
  }
  const rivi = (id: number) => paivat[id] ?? { alku: "", loppu: "" };
  const aseta = (id: number, k: "alku" | "loppu", arvo: string) => setPaivat((x) => ({ ...x, [id]: { ...rivi(id), [k]: arvo } }));
  return (
    <>
      {saa && valitut.size > 0 && (
        <div className="suodatinrivi" data-su-monivalinta={valitut.size}>
          <span>{t("su.valittuja", { n: valitut.size })}</span>
          <label>{t("su.alku")}<input type="date" value={yhteinen.alku} onChange={(e) => setYhteinen({ ...yhteinen, alku: e.target.value })} data-su-yhteinen-alku="" /></label>
          <label>{t("su.loppu")}<input type="date" value={yhteinen.loppu} onChange={(e) => setYhteinen({ ...yhteinen, loppu: e.target.value })} data-su-yhteinen-loppu="" /></label>
          <button type="button" className="nappi" data-su-aseta-valituille=""
            disabled={tallennetaan || (!yhteinen.alku && !yhteinen.loppu) || ristiriita(yhteinen.alku, yhteinen.loppu)}
            onClick={() => void tallenna([...valitut], yhteinen.alku, yhteinen.loppu)}>{t("su.aseta_valituille")}</button>
        </div>
      )}
      <ul style={{ listStyle: "none", padding: 0 }}>
        {paketit.map((p) => {
          const r = rivi(p.id);
          return (
            <li key={p.id} data-su-paketti={p.id} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", padding: "4px 0" }}>
              {saa && <input type="checkbox" checked={valitut.has(p.id)} aria-label={t("su.valitse")}
                onChange={() => setValitut((s) => { const n = new Set(s); if (n.has(p.id)) n.delete(p.id); else n.add(p.id); return n; })} data-su-valinta={p.id} />}
              <a href={`#/tyopaketit?paketti=${p.id}`} data-su-linkki={p.id}><b>#{p.nro}</b> {p.nimi}</a>
              <span className="dim">· {p.tila}</span>
              {saa && <>
                <label className="dim">{t("su.alku")} <input type="date" value={r.alku} onChange={(e) => aseta(p.id, "alku", e.target.value)} data-su-alku={p.id} /></label>
                <label className="dim">{t("su.loppu")} <input type="date" value={r.loppu} onChange={(e) => aseta(p.id, "loppu", e.target.value)} data-su-loppu={p.id} /></label>
                <button type="button" className="nappi" data-su-tallenna={p.id}
                  disabled={tallennetaan || (!r.alku && !r.loppu) || ristiriita(r.alku, r.loppu)}
                  onClick={() => void tallenna([p.id], r.alku, r.loppu)}>{t("su.tallenna")}</button>
                {ristiriita(r.alku, r.loppu) && <span className="huom">{t("su.loppu_ennen_alkua")}</span>}
              </>}
            </li>);
        })}
      </ul>
      {virhe && <p className="huom huom--vika" role="alert" data-su-virhe="">{virhe}</p>}
    </>
  );
}

export function Suunnitelma({ kayttaja }: { kayttaja: Kayttaja }) {
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [kohde, setKohde] = useState<number | "">("");
  const { ylapalkissa } = useKohdeSynkka(kohde, setKohde, { kaikkiSallittu: false });
  const [data, setData] = useState<SuunnitelmaTieto | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [kierros, setKierros] = useState(0);   // T373: suora muokkaus päivittää datan ilman sivun uudelleenlatausta
  const saaMuokata = kayttaja.roolit.some((r) => MUOKKAAJAT.includes(r));

  const klKohteet = useLataus();
  useEffect(() => { klKohteet.seuraa(api.kohteet().then(setKohteet)); }, [klKohteet.kerta]);
  useEffect(() => {
    if (kohde === "") { setData(null); return; }
    let elossa = true;
    api.suunnitelma(kohde).then((r) => { if (elossa) { setData(r); setVirhe(null); } })
      .catch((e) => { if (elossa) { setData(null); setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)); } });
    return () => { elossa = false; };
  }, [kohde, kierros]);

  const osuu = (p: NonNullable<SuunnitelmaTieto["paketit"]>[number], v: SuunnitelmaTieto["viikot"][number]) =>
    (p.suunniteltu_alku ?? p.suunniteltu_loppu)! <= v.loppu && (p.suunniteltu_loppu ?? p.suunniteltu_alku)! >= v.alku;

  return (
    <>
      <header className="head">
        <div className="head__rivi"><h1>{t("valikko.suunnitelma")}</h1></div>
      </header>
      <LatausTila l={klKohteet} nimi="lataus.kohteet" />
      {!ylapalkissa && (
        <div className="suodatinrivi">
          <label className="dim" style={{ fontSize: 13 }}>{t("su.kohde")}{" "}
            <select value={kohde} onChange={(e) => setKohde(e.target.value ? Number(e.target.value) : "")}>
              <option value="">{t("su.valitse_kohde")}</option>
              {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
            </select>
          </label>
        </div>
      )}
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      {kohde === "" && !virhe && <p className="dim">{t("su.valitse_kohde")}</p>}
      {data && (
        <div data-suunnitelma={data.kohde_id} data-suunnitelma-viikkoja={data.viikot.length}>
          {!data.varaa && <p className="huom huom--varoitus" data-suunnitelma-ei-varaa="">{t("su.ei_varaa")}</p>}
          <div className="scroll">
            <table className="tbl suunnitelma__taulu" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>{t("su.tyopaketti")}</th>
                  {data.viikot.map((v) => (
                    <th key={v.alku} data-suunnitelma-viikko={v.alku} style={{ textAlign: "center" }}>
                      {t("su.viikko", { n: v.viikko })}<br />
                      <span className="dim" style={{ fontWeight: 400 }}>{naytaPvm(v.alku)} – {naytaPvm(v.loppu)}</span>
                    </th>
                  ))}
                  <th>{t("nippu.tila")}</th>
                </tr>
              </thead>
              <tbody>
                {data.paketit === undefined ? (
                  <tr><td colSpan={data.viikot.length + 2} className="dim">{t("su.ei_hitsauslokia")}</td></tr>
                ) : data.paketit.length === 0 ? (
                  <tr><td colSpan={data.viikot.length + 2} className="dim" data-suunnitelma-tyhja="">{t("su.ei_paketteja")}</td></tr>
                ) : data.paketit.map((p) => (
                  <tr key={p.id} data-suunnitelma-paketti={p.id}>
                    <td><b>#{p.nro}</b> {p.nimi}
                      <div className="dim">
                        {naytaPvm(p.suunniteltu_alku ?? p.suunniteltu_loppu ?? "")} – {naytaPvm(p.suunniteltu_loppu ?? p.suunniteltu_alku ?? "")}
                        {" · "}{t("su.linjoja", { n: p.linjoja })}{p.tekijoita > 0 && ` · ${t("su.tekijoita", { n: p.tekijoita })}`}
                      </div>
                    </td>
                    {data.viikot.map((v) => (
                      <td key={v.alku} className={osuu(p, v) ? "suunnitelma__jana" : undefined} data-suunnitelma-jana={osuu(p, v) ? "1" : "0"}
                          style={{ textAlign: "center" }}>{osuu(p, v) ? "▬" : ""}</td>
                    ))}
                    <td>
                      <span data-tila={p.tila}>{p.tila}</span>
                      {p.valmiusilmoituksia > 0 && (
                        <div><Tilasana laji="kunnossa">{t("su.valmiusilmoituksia", { n: p.valmiusilmoituksia, m: p.linjoja })}</Tilasana></div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr data-suunnitelma-tunnit="tarve">
                  <th>{t("su.tarve_h")}</th>
                  {data.viikot.map((v) => <td key={v.alku} style={{ textAlign: "center" }} className="mono">{luku(v.tarve)}</td>)}
                  <td></td>
                </tr>
                <tr data-suunnitelma-tunnit="allokoitu">
                  <th>{t("su.allokoitu_h")}</th>
                  {data.viikot.map((v) => <td key={v.alku} style={{ textAlign: "center" }} className="mono">{luku(v.allokoitu)}
                    {v.henkiloita > 0 && <span className="dim"> · {t("su.henkiloa", { n: v.henkiloita })}</span>}</td>)}
                  <td></td>
                </tr>
                <tr data-suunnitelma-tunnit="vaje">
                  <th>{t("su.vaje")}</th>
                  {data.viikot.map((v) => {
                    const ero = v.tarve - v.allokoitu;
                    return <td key={v.alku} style={{ textAlign: "center" }}>
                      {/* Toni 15.10.2026: jos tarvetta ei ole syötetty, «tarve puuttuu» eikä vajetta lasketa. */}
                      {(data.tarve_lahde?.rivit ?? 0) === 0 ? <Tilasana laji="ei_mitattu">{t("su.tarve_puuttuu")}</Tilasana>
                        : v.tarve === 0 && v.allokoitu === 0 ? <Tilasana laji="ei_mitattu">{t("su.ei_tarvetta")}</Tilasana>
                        : ero > 0 ? <Tilasana laji="vika">{t("su.vajetta", { h: luku(ero) })}</Tilasana>
                        : <Tilasana laji="kunnossa">{t("su.katettu")}</Tilasana>}
                    </td>;
                  })}
                  <td></td>
                </tr>
              </tfoot>
            </table>
            {/* T373 (Toni 15.10.2026 klo 18.15): tarpeen ja allokoinnin lähde ruudulla. */}
            <p className="dim" data-suunnitelma-lahde={data.tarve_lahde?.rivit ?? 0}>
              {(data.tarve_lahde?.rivit ?? 0) > 0
                ? t("su.lahde", { rivit: data.tarve_lahde!.rivit, henkiloita: luku(data.tarve_lahde!.henkiloita) })
                : t("su.lahde_ei_tarvetta")}</p>
          </div>
          {data.aikatauluttamattomat.length > 0 && (
            <section style={{ marginTop: 16 }} data-suunnitelma-aikatauluttamattomat={data.aikatauluttamattomat.length}>
              <h2 style={{ fontSize: 15 }}>{t("su.ei_aikataulutettu", { n: data.aikatauluttamattomat.length })}</h2>
              <p className="dim">{t("su.ei_aikataulutettu_selite")}</p>
              <AikatauluttamattomatLista paketit={data.aikatauluttamattomat as { id: number; nro: number; nimi: string; tila: string }[]}
                saa={saaMuokata} paivita={() => setKierros((x) => x + 1)} />
            </section>
          )}
        </div>
      )}
    </>
  );
}
